import React, { useState } from 'react';
import { ArticleDraft } from '@/hooks/useArticleDrafts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, ExternalLink, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface DraftCardProps {
  draft: ArticleDraft;
  onApprove: (draftId: string) => void;
  onReject: (draftId: string, reason: string) => void;
}

export const DraftCard: React.FC<DraftCardProps> = ({ draft, onApprove, onReject }) => {
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleReject = () => {
    if (rejectionReason.trim()) {
      onReject(draft.id, rejectionReason);
      setRejectDialogOpen(false);
      setRejectionReason('');
    }
  };

  return (
    <>
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <CardTitle className="text-2xl text-foreground">{draft.title}</CardTitle>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary" className="font-medium">
                  {draft.region}
                </Badge>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(draft.created_at), 'PPP', { locale: nb })}
                </span>
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  Score: {draft.composite_score?.toFixed(2) || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Article Content */}
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-foreground">{draft.content}</div>
          </div>

          {/* Sources */}
          <div className="border-t border-border pt-4">
            <h4 className="font-semibold text-foreground mb-3">
              Kilder ({draft.source_articles?.length || 0})
            </h4>
            <div className="space-y-2">
              {draft.source_articles?.map((source: any, idx: number) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 p-3 bg-muted rounded-md text-sm"
                >
                  <ExternalLink className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-medium break-words"
                    >
                      {source.title || source.url}
                    </a>
                    <p className="text-muted-foreground text-xs mt-1">
                      {source.domain} • {source.publishDate ? format(new Date(source.publishDate), 'PPP', { locale: nb }) : 'Ukjent dato'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Event Clusters */}
          {draft.event_clusters && draft.event_clusters.length > 0 && (
            <div className="border-t border-border pt-4">
              <h4 className="font-semibold text-foreground mb-3">
                Hendelser ({draft.event_clusters.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {draft.event_clusters.map((cluster: any, idx: number) => (
                  <Badge key={idx} variant="outline">
                    {cluster.sources?.length || 0} kilder
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          {draft.generation_metadata && (
            <div className="border-t border-border pt-4">
              <details className="text-sm">
                <summary className="cursor-pointer font-semibold text-muted-foreground hover:text-foreground">
                  Genereringsmetadata
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-x-auto">
                  {JSON.stringify(draft.generation_metadata, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex justify-end gap-3 border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={() => setRejectDialogOpen(true)}
            className="gap-2"
          >
            <XCircle className="h-4 w-4" />
            Avvis
          </Button>
          <Button
            onClick={() => onApprove(draft.id)}
            className="gap-2"
          >
            <CheckCircle className="h-4 w-4" />
            Godkjenn og Publiser
          </Button>
        </CardFooter>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avvis artikkelutkast</DialogTitle>
            <DialogDescription>
              Oppgi en grunn til hvorfor dette utkastet avvises. Dette vil bli lagret for
              analyse og forbedring.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="F.eks.: Feil informasjon, dårlig kvalitet, ikke relevant..."
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
            >
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectionReason.trim()}
            >
              Bekreft avvisning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
